/**
 * Ostatni token po spacjach musi wyglądać na symbol pozycji katalogowej (cyfry + myślniki itd.),
 * a nie na dopisek opisowy (np. „z.sz.2.3.”, „analiza indywidualna”) — wtedy obcinamy tokeny od końca.
 */
function isLikelyKnrSymbolToken(s: string): boolean {
  if (!s || !/[0-9]/.test(s) || !/^[\dA-Za-z.\-]+$/.test(s)) return false;
  /* Symbole w bazie zaczynają się od cyfry; dopiski typu „z.sz.2.3.” zaczynają się od litery. */
  if (!/^\d/.test(s)) return false;
  /* Pojedyncza cyfra to zwykle fragment opisu („wariant 2”), nie symbol KNR. */
  if (/^\d$/.test(s)) return false;
  return true;
}

/**
 * Dostawcy zapisują „wariant W” jako `KNR-W 2-02 …`, a w bazie jest `KNR 2-02W …`.
 * Przykład: `KNR-W 2-02` → `KNR 2-02W`; `KNNR-W 4-01` → `KNNR 4-01W`.
 */
function normalizeKnrWCatalogId(knr: string): string {
  const t = knr.trim().replace(/\s+/g, ' ');
  const m = t.match(/^(KNR|KNNR)-W\s+(\S+)(?:\s+(.*))?$/i);
  if (!m) return knr;
  const kind = m[1].toUpperCase() === 'KNNR' ? 'KNNR' : 'KNR';
  const tom = m[2] ?? '';
  const tail = (m[3] ?? '').trim();
  const head = `${kind} ${tom}W`;
  return tail ? `${head} ${tail}` : head;
}

/**
 * Parsuje pole „Podstawa” (np. `KNR 2-01 0233-09`): ostatni segment to symbol katalogu,
 * wszystko przed ostatnią spacją — identyfikator KNR (np. `KNR 2-01`).
 * Tekst `KNR 4-04 0603-07 z.sz.2.3.` → knr `KNR 4-04`, symbol `0603-07` (suffix opisowy odcinany).
 * Zapis `KNR-W 2-02 0525-01` → knr `KNR 2-02W`, symbol `0525-01` (zgodnie z katalogiem w bazie).
 */
export function parseKnrAndSymbolFromPodstawa(text: string): { knr: string; symbol: string } | null {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length < 3) return null;
  const parts = t.split(/\s+/);
  if (parts.length < 2) return null;

  while (parts.length >= 2 && !isLikelyKnrSymbolToken(parts[parts.length - 1] ?? '')) {
    parts.pop();
  }
  if (parts.length < 2) return null;

  const symbol = parts[parts.length - 1] ?? '';
  const knrRaw = parts.slice(0, -1).join(' ');
  if (!symbol || !knrRaw) return null;
  return { knr: normalizeKnrWCatalogId(knrRaw), symbol };
}

/**
 * Kanoniczna postać symbolu katalogu do dopasowywania. Lustrzane odbicie reguł
 * zapytania MySQL w `backend/src/controllers/knrController.js` (RMS_QUERY + fallback):
 * - dopasowanie z myślnikami i bez (`REPLACE(symbol, '-', '')`),
 * - rozszerzenie krótkiej formy `NNNN-NN` ↔ pełnej `NNNN-NN00`.
 *
 * Przykłady (oba zwracają „035403”):
 *   `0354-03`   → `035403`
 *   `0354-0300` → `035403`
 *   `0354-0301` → `03540301` (inny wariant — nie skracamy)
 */
export function canonicalKnrSymbol(symbol: string): string {
  const noDash = symbol.trim().toLocaleLowerCase('pl-PL').replace(/-/g, '');
  if (/^\d{8}$/.test(noDash) && noDash.endsWith('00')) return noDash.slice(0, 6);
  return noDash;
}

/**
 * Klucz dopasowania dwóch zapisów „Podstawy” (np. między pozycją kosztorysu a biblioteką
 * „zweryfikowanych”) — używa `parseKnrAndSymbolFromPodstawa` + `canonicalKnrSymbol`,
 * a do KNR usuwa wszystkie białe znaki (mirror `cleanKnrCompact` z kontrolera MySQL),
 * dzięki czemu `KNR 4-01` ↔ `KNR4-01` ↔ `knr 4-01` dają ten sam wynik.
 *
 * Zwraca `null`, gdy `Podstawa` nie zawiera rozpoznawalnego symbolu KNR
 * (np. „Wycena indywidualna”, „Kalkulacja własna”) — wtedy w warstwie wyższej
 * można fallbackować do porównania pełnego znormalizowanego tekstu.
 */
export function canonicalizeKnrPodstawaForMatch(text: string): string | null {
  const parsed = parseKnrAndSymbolFromPodstawa(text);
  if (!parsed) return null;
  const knrCompact = parsed.knr.toLocaleLowerCase('pl-PL').replace(/\s+/g, '');
  const symCanonical = canonicalKnrSymbol(parsed.symbol);
  if (!knrCompact || !symCanonical) return null;
  return `${knrCompact}|${symCanonical}`;
}
